import { useEffect, useState } from 'react';

import type { Team } from '@/core/schemas';

import { toPlayableSnippet } from './walkOnPlayer';

interface WalkOnOverlayProps {
  team: Team;
  maxDurationSeconds: number;
  onFinished: () => void;
}

/**
 * Full-screen team entrance: the team is announced and, if a playable link
 * exists, its walk-on snippet plays. A timer closes the overlay after the
 * capped duration so nothing runs longer than allowed, whether or not the
 * embed reports its own end.
 */
export function WalkOnOverlay({ team, maxDurationSeconds, onFinished }: WalkOnOverlayProps) {
  // Remounted per team via `key` in the parent, so the initial value is always
  // correct and needs no reset effect.
  const snippet = toPlayableSnippet(team.walkOnSong, maxDurationSeconds);
  const [remaining, setRemaining] = useState(snippet?.durationSeconds ?? 6);

  useEffect(() => {
    const total = snippet?.durationSeconds ?? 6;
    const finish = window.setTimeout(onFinished, total * 1000);
    const tick = window.setInterval(() => {
      setRemaining((value) => Math.max(0, value - 1));
    }, 1000);

    return () => {
      window.clearTimeout(finish);
      window.clearInterval(tick);
    };
  }, [snippet?.durationSeconds, onFinished]);

  return (
    <div className="bg-canvas/95 fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 p-8 backdrop-blur">
      {snippet !== null && (
        <iframe
          title={`Einlaufsong ${team.name}`}
          src={snippet.embedUrl}
          allow="autoplay; encrypted-media"
          className="pointer-events-none absolute h-px w-px opacity-0"
        />
      )}

      <p className="text-brand text-2xl font-semibold tracking-widest uppercase">Jetzt am Board</p>
      <h1 className="text-center text-6xl font-black tracking-tight sm:text-8xl">{team.name}</h1>
      <p className="text-fg-muted text-3xl">{team.fireDepartment}</p>

      <div className="text-fg-subtle mt-4 flex items-center gap-3 text-lg">
        <span aria-hidden className="bg-brand size-3 animate-pulse rounded-full" />
        <span>{team.walkOnSong.title}</span>
      </div>

      <button
        type="button"
        onClick={onFinished}
        className="text-fg-subtle hover:text-fg absolute right-6 bottom-6 text-sm underline"
      >
        Überspringen ({remaining}s)
      </button>
    </div>
  );
}
